import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { kArtifactViewableUrlApiPath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { ArtifactVersionViewableUrlResponseSchema, type ArtifactVersionViewableUrlResponse } from '@/artifacts/schemas'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

export async function getArtifactVersionViewableUrl(artifactVersionId: string): Promise<ArtifactVersionViewableUrlResponse> {
  const sentryTransactionId = nanoid()
  const token = await getAuth().currentUser?.getIdToken()

  if (!token) {
    throw new Error('User not authenticated')
  }

  const url = `${kPaxtonAppApiRESTBaseUrl()}${kArtifactViewableUrlApiPath.replace('{artifactVersionId}', encodeURIComponent(artifactVersionId))}`
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Transaction-Id': sentryTransactionId,
    },
  })

  const { data, parseError } = await readResponseJson(response)
  if (!response.ok) {
    const errorData = parseError ? {} : data
    const detail = formatArtifactErrorDetail(errorData)
    throw new ApiError(response.status, detail || `Failed to create artifact viewable URL: ${response.status}`)
  }

  if (parseError) {
    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: sentryTransactionId })
      Sentry.captureException(new Error('Artifact viewable URL response JSON parse failed'), {
        extra: {
          ...getResponseDebugContext(response, url),
          artifactVersionId,
          parseError: formatUnknownError(parseError),
        },
      })
    })

    throw new ApiResponsePayloadError(response.status, 'Invalid artifact viewable URL response JSON')
  }

  const parsed = ArtifactVersionViewableUrlResponseSchema.safeParse(data)
  if (!parsed.success) {
    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: sentryTransactionId })
      Sentry.captureException(new Error('Artifact viewable URL response schema validation failed'), {
        extra: {
          ...getResponseDebugContext(response, url),
          artifactVersionId,
          responseKeys: getTopLevelKeys(data),
          issues: parsed.error.issues,
        },
      })
    })

    throw new ApiResponsePayloadError(response.status, 'Invalid artifact viewable URL response shape', parsed.error.issues)
  }

  return parsed.data
}

export async function fetchArtifactMarkdownFromViewableUrl(artifactVersionId: string): Promise<string> {
  const { viewable_url } = await getArtifactVersionViewableUrl(artifactVersionId)
  const response = await fetch(viewable_url)

  if (!response.ok) {
    throw new Error(`Failed to fetch artifact markdown: ${response.status}`)
  }

  return response.text()
}

function formatArtifactErrorDetail(data: unknown): string | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null

  const detail = (data as { detail?: unknown }).detail
  if (typeof detail === 'string') return detail
  if (detail === undefined || detail === null) return null
  return JSON.stringify(detail)
}
